import React from "react";
import PaymentDialogClose from "./PaymentDialogClose";
import PublicBillDialog from "./PublicBillDialog";
import "./PayHandoffModal.css";
import usePaymentCountdown from "../hooks/usePaymentCountdown";
import ActionButton from "./base/ActionButton";
import { CheckCircle2, ArrowUpRight, ArrowLeft } from 'lucide-react';


export interface PayHandoffTarget {
  label: string;
  avatarUrl?: string;
  amountLabel?: string;
  breakdown?: { label: string; amountLabel: string }[];
  status?: string;
}

interface PayHandoffModalProps {
  className?: string;
  open: boolean;
  confirmation?: React.ReactNode;
  startWithCountdown?: boolean;
  opensInNewTab?: boolean;
  onCancel: () => void;
  /** Called directly from the acknowledgement click after the reading pause. */
  onContinue: () => void | Promise<unknown>;
  payeeName: string;
  payeeHandle?: string | null;
  payeeAvatarUrl?: string;
  /** Pre-formatted total, e.g. "$42.50". */
  amountLabel: string;
  /** People covered by this payment — or bill titles when payingForMode is "bills". */
  payingFor: PayHandoffTarget[];
  payingForMode?: "people" | "bills";
  ctaLabel?: string;
  onMarkPaid?: () => void;
  markPaidLabel?: string;
  busy?: boolean;
  hideContinue?: boolean;
  continueDisabled?: boolean;
  paymentOptions?: React.ReactNode;

  title?: string;
}

// Recipient header, "paying for" chips, and the return acknowledgement are
// exported so non-Radix pay surfaces (e.g. the UPI app-picker modal) can
// reuse the exact same design without stacking a second dialog.

export function PayHandoffRecipient({
  name,
  handle,
  avatarUrl,
}: {
  name: string;
  /** Displayed verbatim — pass "@user" for Venmo, the VPA for UPI. */
  handle?: string | null;
  avatarUrl?: string;
}) {
  return (
    <div className="handoff-recipient flex items-center gap-3">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={name}
          className="h-11 w-11 flex-shrink-0 rounded-full object-cover"
        />
      ) : (
        <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-slate-200 text-base font-semibold text-slate-700">
          {(name || "?").slice(0, 1).toUpperCase()}
        </span>
      )}
      <div className="min-w-0">
        <div className="truncate text-[15px] font-bold text-slate-900">
          Pay to: {name}
        </div>
        {handle ? (
          <div className="truncate text-[13px] text-slate-500">{handle}</div>
        ) : null}
      </div>
    </div>
  );
}

const MAX_VISIBLE_TARGETS = 3;

export function PayHandoffPayingFor({
  payingFor,
  mode = "people",
}: {
  payingFor: PayHandoffTarget[];
  mode?: "people" | "bills";
}) {
  if (payingFor.length === 0) return null;
  const isBills = mode === "bills";
  const visible = payingFor.slice(0, MAX_VISIBLE_TARGETS);
  const overflow = payingFor.length - visible.length;
  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Paying for
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {visible.map((target, idx) => (
          <span
            key={`${target.label}-${idx}`}
            className={`inline-flex max-w-full items-center gap-1.5 rounded-full bg-slate-100 py-1 text-[13px] font-semibold text-slate-700 ${
              isBills ? "px-2.5" : "pl-1 pr-2.5"
            }`}
          >
            {!isBills &&
              (target.avatarUrl ? (
                <img
                  src={target.avatarUrl}
                  alt={target.label}
                  className="h-6 w-6 flex-shrink-0 rounded-full object-cover"
                />
              ) : (
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-300 text-[11px] font-semibold text-slate-700">
                  {(target.label || "?").slice(0, 1).toUpperCase()}
                </span>
              ))}
            <span className="truncate">{target.label}</span>
          </span>
        ))}
        {overflow > 0 && (
          <span className="text-[13px] text-slate-500">
            and {overflow} more
          </span>
        )}
      </div>
    </div>
  );
}

export interface PayHandoffReminderProps {
  onEditPeople?: () => void;
  isPaid?: boolean;
  paidHeading?: string;
  title?: string;
  amountLabel: string;
  payeeName: string;
  payeeHandle?: string | null;
  ctaLabel?: string;
  onMarkPaid?: () => void;
  markPaidLabel?: string;
  busy?: boolean;
  hideContinue?: boolean;
  continueDisabled?: boolean;
  paymentOptions?: React.ReactNode;

  startWithCountdown?: boolean;
  opensInNewTab?: boolean;
  payingFor?: PayHandoffTarget[];
  onCancel: () => void;
  onContinue: () => void | Promise<unknown>;
}

// Mount once per handoff. Motion follows the same foreground-only timer as the CTA.
function PayHandoffCountdown({ payingFor, ctaLabel = "Continue to Venmo", opensInNewTab = false, onCancel, onContinue, onBack }: PayHandoffReminderProps & { onBack: () => void }) {
  const { seconds, progress } = usePaymentCountdown();
  const [reducedMotion, setReducedMotion] = React.useState(() => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  const titleRef = React.useRef<HTMLHeadingElement>(null);
  const submitted = React.useRef(false);
  const [failed, setFailed] = React.useState(false);
  const [launched, setLaunched] = React.useState(false);
  React.useEffect(() => { titleRef.current?.focus(); }, []);
  const launch = () => {
    if (seconds > 0 || submitted.current) return;
    submitted.current = true;
    setFailed(false);
    setLaunched(true);
    const fail = () => { submitted.current = false; setLaunched(false); setFailed(true); };
    // Keep the browser's user activation: no timer, effect, or await before this.
    try {
      const result = onContinue();
      if (result && typeof result.then === "function") {
        void result.catch(fail);
      }
    } catch {
      fail();
    }
  };
  const appName = ctaLabel.replace(/^Continue (?:to|on) /, '');
  return (
    <div className="handoff-countdown-content relative" onKeyDown={(event) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); onCancel(); }
      if (event.key === 'Tab') {
        const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled), input:not(:disabled), a[href], summary'));
        if (!buttons.length) { event.preventDefault(); return; }
        const index = buttons.indexOf(document.activeElement as HTMLElement);
        if (event.shiftKey && index <= 0) { event.preventDefault(); buttons[buttons.length - 1]?.focus(); }
        else if (!event.shiftKey && index === buttons.length - 1) { event.preventDefault(); buttons[0]?.focus(); }
      }
    }}>
      <PaymentDialogClose onClose={onCancel} />
      <button type="button" className="handoff-back" disabled={launched} onClick={onBack}><ArrowLeft size={18} aria-hidden="true" /> Back</button>
      <p className="handoff-eyebrow">After paying in {appName}</p>
      <h2 ref={titleRef} tabIndex={-1} className="handoff-return-title">Come back to mark {payingFor && payingFor.length > 1 ? 'these shares' : 'your share'} as paid.</h2>
      <p className="handoff-explanation">Your payment won’t update this bill automatically.</p>
      <div className="handoff-reading" aria-hidden="true">
        <div className="handoff-reading-track"><span style={{ transform: `scaleX(${reducedMotion ? 1 - seconds / 3 : progress})` }} /></div>
        <p>{seconds ? `Take a moment to read · ${seconds}s` : 'Ready when you are'}</p>
      </div>
      {failed && <p role="alert" className="handoff-launch-error">Couldn’t open {appName}. {opensInNewTab ? 'Allow pop-ups for this site, then try again.' : 'Try again.'}</p>}
      <button type="button" disabled={seconds > 0 || launched} onClick={launch} className="handoff-primary handoff-acknowledge">
        <span>{failed ? `Try opening ${appName} again` : launched ? `${appName} opened` : `Got it — open ${appName}`}</span>
        {opensInNewTab && <ArrowUpRight size={18} aria-hidden="true" />}
      </button>
      {opensInNewTab && <p className="handoff-tab-note">Opens in a new tab. Keep this bill open.</p>}
      <span role="status" className="sr-only">{seconds === 0 ? `Ready. Select Got it — open ${appName} to continue.` : 'Read the reminder. Continue is available in three seconds.'}</span>
    </div>
  );
}

export function PayHandoffReminder(props: PayHandoffReminderProps) {
  const [counting, setCounting] = React.useState(!!props.startWithCountdown);
  const titleRef = React.useRef<HTMLHeadingElement>(null);
  React.useEffect(() => { titleRef.current?.focus(); }, []);
  if (counting) return <PayHandoffCountdown {...props} onBack={() => setCounting(false)} />;
  return (
    <div className="handoff-review relative text-left" onKeyDown={(event) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); props.onCancel(); }
      if (event.key === 'Tab') {
        const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled), input:not(:disabled), a[href], summary'));
        if (!buttons.length) { event.preventDefault(); return; }
        const index = buttons.indexOf(document.activeElement as HTMLElement);
        if (event.shiftKey && index <= 0) { event.preventDefault(); buttons[buttons.length - 1]?.focus(); }
        else if (!event.shiftKey && index === buttons.length - 1) { event.preventDefault(); buttons[0]?.focus(); }
      }
    }}>
      <PaymentDialogClose onClose={props.onCancel} disabled={props.busy} />
      <h2 ref={titleRef} tabIndex={-1} className={props.isPaid ? 'handoff-paid-heading pr-10 outline-none' : props.title === '' ? 'sr-only' : 'handoff-heading pr-10 mb-5 mt-0 text-[24px] font-bold tracking-tight text-slate-900 outline-none'}>{props.isPaid ? <><CheckCircle2 size={28} aria-hidden="true" /> {props.paidHeading || 'Marked as paid'}</> : props.title === '' ? 'Payment details' : props.title || 'Review payment'}</h2>
      {!props.isPaid && <div className="pr-10"><PayHandoffRecipient name={props.payeeName} handle={props.payeeHandle} /></div>}
      {!!props.payingFor?.length && <div className={props.isPaid ? 'mb-5' : 'my-5 border-t border-slate-100 pt-4'}>
        {!props.isPaid && <div className={`handoff-people-heading${props.onEditPeople ? ' handoff-people-heading--editable' : ''}`}>
          <p className="mb-3 mt-0 text-xs font-semibold uppercase tracking-wide text-slate-500">{props.onEditPeople ? 'Who’s included' : 'Paying for'}</p>
          {props.onEditPeople && <button type="button" className="handoff-edit-people" disabled={props.busy} onClick={props.onEditPeople}>
            <span><span aria-hidden="true">+</span> Add someone else</span>
          </button>}
        </div>}
        {props.payingFor.map((person, index) => <div key={`${person.label}:${index}`} className="handoff-person border-b border-slate-100 py-3 last:border-0">
          <div className="flex items-center gap-3 text-sm text-slate-700">
            <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 font-semibold text-slate-600">{person.label.slice(0, 1)}</span>
            <div className="min-w-0 flex-1"><span className="font-semibold text-slate-900">{person.label}</span>{person.status && !props.isPaid && <p className="m-0 text-xs text-slate-500">{person.status}</p>}</div>
            {person.amountLabel && <strong className="shrink-0 text-lg tabular-nums text-slate-900">{person.amountLabel}</strong>}
          </div>
          {!!person.breakdown?.length && <div className="handoff-breakdown mt-2 pl-12" role="list" aria-label={`${person.label} breakdown`}>{person.breakdown.map((row, i) => <div role="listitem" key={i} className="flex justify-between gap-4 py-1 text-sm text-slate-500"><span>{row.label}</span><span className="shrink-0 tabular-nums">{row.amountLabel}</span></div>)}</div>}
        </div>)}
      </div>}
      {(props.payingFor?.length !== 1 || !props.payingFor[0].amountLabel) && <div className="handoff-total my-5 flex items-center justify-between gap-3 rounded-2xl bg-slate-50 p-4"><span className="font-semibold text-slate-600">Total</span><strong className="text-2xl tabular-nums text-slate-900">{props.amountLabel}</strong></div>}
      {props.paymentOptions}
      {!props.hideContinue && <ActionButton disabled={props.continueDisabled || props.busy} label={props.ctaLabel || 'Continue to Venmo'} onPress={() => setCounting(true)} className="handoff-primary w-full border-[#006dcc] bg-[#006dcc] py-3.5 text-base text-white hover:bg-[#005eb3]" />}
      {props.onMarkPaid && (props.isPaid ? <div className="handoff-status-action">
        <button type="button" className="handoff-status-link" disabled={props.busy} onClick={props.onMarkPaid}>{props.busy ? 'Updating…' : props.markPaidLabel || 'Mark as unpaid'}</button>
      </div> : <div className="handoff-record-payment mt-5 border-t border-slate-100 pt-3"><p className="m-0 text-center text-xs text-slate-500">Already paid?</p><ActionButton label={props.busy ? 'Updating…' : props.markPaidLabel || 'Mark as paid'} disabled={props.busy} onPress={props.onMarkPaid} className="handoff-secondary w-full border-slate-200 bg-white py-3 text-slate-700" /></div>)}
    </div>
  );
}

// Same shell as every other public bill surface: centered on desktop, a draggable sheet on phones.
export default function PayHandoffModal({ className = '', open, onCancel, onContinue, payeeName, payeeHandle, amountLabel, ctaLabel, payingFor, startWithCountdown, opensInNewTab, confirmation, onMarkPaid, busy, continueDisabled, paymentOptions }: PayHandoffModalProps) {
  return (
    <PublicBillDialog open={open} onClose={onCancel} title="Before you pay" contentHasClose busy={busy} className={className}>
      {confirmation || (open && <PayHandoffReminder opensInNewTab={opensInNewTab} continueDisabled={continueDisabled} paymentOptions={paymentOptions} onMarkPaid={onMarkPaid} busy={busy} startWithCountdown={startWithCountdown} key={`${payeeName}:${amountLabel}:${JSON.stringify(payingFor)}`} amountLabel={amountLabel} payingFor={payingFor} payeeName={payeeName} payeeHandle={payeeHandle ? `@${payeeHandle.replace(/^@/, '')}` : undefined} ctaLabel={ctaLabel} onContinue={onContinue} onCancel={onCancel} />)}
    </PublicBillDialog>
  );
}
